import { useState, useEffect } from 'react';

import Btn from '../ui/Btn';
import { Card, Badge, Spinner } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import SearchableSelect from '../ui/SearchableSelect';
import ExpirationField from './ExpirationField';
import RetryClassificationButton from './RetryClassificationButton';
import { supabase } from '../../lib/supabase';
import { api } from '../../lib/api';
import { useBreakpoint, useIsMobile } from '../../lib/useBreakpoint';

/**
 * FileViewerModal
 *
 * Props:
 *   doc            – document_uploads row (joined with template_label optional)
 *   templates      – list of available templates for reassignment
 *   canReview      – boolean (admin can approve/reject/reassign)
 *   onClose()
 *   onChanged(updatedDoc)  – called after approve/reject/reassign succeeds
 */
export default function FileViewerModal({ doc, templates = [], canReview, onClose, onChanged }) {
  const { width } = useBreakpoint();
  const isMobile = useIsMobile();
  // Side panel was previously toggled to column below 900px. Use the same
  // cutoff so existing behavior is preserved on tablets.
  const stackLayout = width < 900;
  const [busy, setBusy]       = useState(null);   // 'approve' | 'reject' | 'reassign' | null
  const [reassign, setReassign] = useState('');   // template_id or '__unclassified__'
  const [error, setError]     = useState(null);
  const [rejectOpen, setRejectOpen]     = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // ── Detect file type ──
  // ── Resolve a viewable URL from the storage_path ──
  // Old (vanilla app) and new (v2) docs both use storage_path — never file_url.
  // We generate a signed URL good for 1 hour each time the modal opens.
  const [fileUrl, setFileUrl]       = useState('');
  const [urlError, setUrlError]     = useState(null);
  const [urlLoading, setUrlLoading] = useState(false);

  useEffect(() => {
    if (!doc) return;
    // Support both old `storage_path` (canonical) and any legacy `file_url` field
    const path = doc.storage_path || doc.file_url || '';
    if (!path) {
      setFileUrl('');
      setUrlError('No file path on this document.');
      return;
    }

    // If somehow it's already a full URL, use it directly
    if (/^https?:\/\//i.test(path)) {
      setFileUrl(path);
      setUrlError(null);
      return;
    }

    // Generate a fresh signed URL from the client-docs bucket (1 hour expiry).
    // Try multiple path candidates in order — handles cases where:
    //  - new uploads have full team/member/filename prefix
    //  - old uploads may have stored just the filename, expecting code to add the prefix
    //  - some legacy paths may have `client-docs/` baked into the path itself
    let cancelled = false;
    setUrlLoading(true);
    setUrlError(null);

    const stripped = path.replace(/^\/+/, '').replace(/^client-docs\//, '');
    const candidates = [stripped];
    // If stripped doesn't already start with the team_id, also try with the prefix
    if (doc.team_id && doc.member_id && !stripped.startsWith(doc.team_id)) {
      candidates.push(`${doc.team_id}/${doc.member_id}/${stripped}`);
    }

    (async () => {
      let lastErr = null;
      for (const candidate of candidates) {
        try {
          const { data, error } = await supabase.storage
            .from('client-docs')
            .createSignedUrl(candidate, 3600);
          if (cancelled) return;
          if (error) { lastErr = error; continue; }
          if (data?.signedUrl) {
            setFileUrl(data.signedUrl);
            setUrlError(null);
            setUrlLoading(false);
            return;
          }
        } catch (e) {
          lastErr = e;
        }
      }
      // All candidates failed
      if (cancelled) return;
      console.error('Could not generate signed URL. Tried:', candidates, 'Last error:', lastErr);
      setUrlError(`Couldn't find this file in storage. Tried: ${candidates.join(' and ')}`);
      setFileUrl('');
      setUrlLoading(false);
    })();
    return () => { cancelled = true; };
  }, [doc?.id, doc?.storage_path]);
  // Compute extension from the canonical source (filename / path), NOT the signed URL —
  // signed URLs have ?token=... that breaks the .pop() heuristic.
  const sourceForExt = doc?.original_filename || doc?.storage_path || '';
  const ext = (sourceForExt.split('?')[0].split('.').pop() || '').toLowerCase();
  const isPdf   = ext === 'pdf';
  const isImage = ['png','jpg','jpeg','gif','webp','heic','svg'].includes(ext);

  // Initialize reassign select with current value
  useEffect(() => {
    if (doc?.template_id) setReassign(doc.template_id);
    else setReassign('__unclassified__');
  }, [doc?.id]);

  if (!doc) return null;

  // ── Actions ──
  const approve = async () => {
    setError(null); setBusy('approve');
    try {
      // If user changed the template via the dropdown, save that first
      const updates = { status: 'approved', reviewed_at: new Date().toISOString() };
      if (reassign && reassign !== '__unclassified__' && reassign !== doc.template_id) {
        updates.template_id = reassign;
        const tpl = templates.find(t => t.id === reassign);
        if (tpl) updates.display_name = tpl.label;
      }
      const { data, error: err } = await supabase
        .from('document_uploads')
        .update(updates)
        .eq('id', doc.id)
        .select()
        .single();
      if (err) throw err;
      onChanged?.(data);
      onClose();
    } catch (err) {
      console.error('Approve failed:', err);
      setError('Could not approve: ' + (err.message || 'unknown error'));
      setBusy(null);
    }
  };

  const reject = () => {
    setRejectReason('');
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    setError(null);
    setBusy('reject');
    try {
      const reason = rejectReason.trim() || 'No reason provided';
      const { data, error: err } = await supabase
        .from('document_uploads')
        .update({
          status: 'rejected',
          admin_notes: reason,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', doc.id)
        .select()
        .single();
      if (err) throw err;

      // Best-effort: notify the uploader (don't block the UI on this).
      // Backend expects { upload_id, new_status, team_id, member_id, admin_notes }
      api('/api/send-notification', {
        upload_id: doc.id,
        new_status: 'rejected',
        team_id: doc.team_id,
        member_id: doc.member_id,
        admin_notes: reason,
      }).catch(e => console.warn('Rejection notification failed:', e));

      setRejectOpen(false);
      onChanged?.(data);
      onClose();
    } catch (err) {
      console.error('Reject failed:', err);
      setError('Could not reject: ' + (err.message || 'unknown error'));
      setBusy(null);
    }
  };

  const saveReassign = async () => {
    setError(null); setBusy('reassign');
    try {
      const updates = {};
      if (reassign === '__unclassified__') {
        updates.template_id = null;
        updates.display_name = null;
      } else {
        updates.template_id = reassign;
        const tpl = templates.find(t => t.id === reassign);
        if (tpl) updates.display_name = tpl.label;
      }
      const { data, error: err } = await supabase
        .from('document_uploads')
        .update(updates)
        .eq('id', doc.id)
        .select()
        .single();
      if (err) throw err;
      onChanged?.(data);
      setBusy(null);
    } catch (err) {
      console.error('Reassign failed:', err);
      setError('Could not reassign: ' + (err.message || 'unknown error'));
      setBusy(null);
    }
  };

  const reassignChanged = reassign !== (doc.template_id || '__unclassified__');
  const statusBadge = {
    in_review:  <Badge variant="warn"   dot>In review</Badge>,
    approved:   <Badge variant="ok"     dot>Approved</Badge>,
    rejected:   <Badge variant="danger" dot>Rejected</Badge>,
    classifying:<Badge variant="info"   dot>Classifying</Badge>,
  }[doc.status] || <Badge variant="neutral">{doc.status}</Badge>;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10, 15, 46, 0.65)',
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: isMobile ? 'none' : '1px solid var(--border)',
          borderRadius: isMobile ? 0 : 'var(--r-xl)',
          boxShadow: 'var(--shadow-xl)',
          width: '100%',
          maxWidth: isMobile ? 'none' : 1100,
          maxHeight: isMobile ? '100dvh' : '92vh',
          height: isMobile ? '100dvh' : 'auto',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 14, fontWeight: 700,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {doc.original_filename || 'Document'}
            </div>
            <div style={{
              fontSize: 11.5, color: 'var(--text-3)', marginTop: 2,
              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            }}>
              {statusBadge}
              {doc.display_name && <span>· Classified as <strong style={{ color: 'var(--text-2)' }}>{doc.display_name}</strong></span>}
              <span>· Uploaded {timeAgo(doc.created_at)}</span>
            </div>
          </div>
          <Btn
            variant="outline"
            size="sm"
            icon={Icon.external}
            onClick={() => window.open(fileUrl, '_blank', 'noopener')}
            disabled={!fileUrl}
          >
            Open in new tab
          </Btn>
          <Btn variant="ghost" size="sm" icon={Icon.x} onClick={onClose} title="Close" />
        </div>

        {/* Body — preview + side panel */}
        <div style={{
          display: 'flex', flex: 1, minHeight: 0,
          flexDirection: stackLayout ? 'column' : 'row',
        }}>
          {/* Preview area */}
          <div style={{
            flex: 1, minHeight: 0,
            background: 'var(--surface-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'auto', padding: 12,
          }}>
            {urlLoading ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                color: 'var(--text-3)', fontSize: 13,
              }}>
                <Spinner size={18} color="var(--accent)" />
                Loading file…
              </div>
            ) : urlError ? (
              <Card style={{ padding: 32, textAlign: 'center', maxWidth: 400 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 14, margin: '0 auto 14px',
                  background: 'var(--danger-bg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon.alert size={26} stroke="var(--danger)" />
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  Couldn't load file
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 14, lineHeight: 1.5 }}>
                  {urlError}
                </div>
                {doc?.storage_path && (
                  <div style={{
                    fontSize: 11, color: 'var(--text-muted)',
                    fontFamily: 'var(--font-mono)',
                    background: 'var(--surface-2)', padding: '6px 10px',
                    borderRadius: 6, wordBreak: 'break-all', textAlign: 'left',
                  }}>
                    {doc.storage_path}
                  </div>
                )}
              </Card>
            ) : !fileUrl ? (
              <NoFileFallback />
            ) : isPdf ? (
              <iframe
                src={fileUrl}
                title={doc.original_filename}
                style={{
                  width: '100%', height: '100%', minHeight: 480,
                  border: 'none', background: '#fff',
                  borderRadius: 8,
                }}
              />
            ) : isImage ? (
              <img
                src={fileUrl}
                alt={doc.original_filename}
                style={{
                  maxWidth: '100%', maxHeight: '100%',
                  objectFit: 'contain', borderRadius: 8,
                  boxShadow: 'var(--shadow-md)',
                }}
              />
            ) : (
              <UnsupportedFallback url={fileUrl} ext={ext} />
            )}
          </div>

          {/* Side panel */}
          <div style={{
            width: stackLayout ? '100%' : 320,
            borderLeft: stackLayout ? 'none' : '1px solid var(--border)',
            borderTop: stackLayout ? '1px solid var(--border)' : 'none',
            padding: 18,
            display: 'flex', flexDirection: 'column', gap: 14,
            overflowY: 'auto',
            background: 'var(--surface)',
          }}>
            {error && (
              <div style={{
                padding: '8px 10px',
                background: 'var(--danger-bg)',
                border: '1px solid rgba(239,68,68,0.22)',
                color: 'var(--danger)', borderRadius: 8,
                fontSize: 12,
              }}>
                {error}
              </div>
            )}

            {/* Classification failed callout (only when schema field set) */}
            {doc.classification_status === 'classification_failed' && (
              <RetryClassificationButton
                doc={doc}
                teamId={doc.team_id}
                onUpdated={(updated) => onChanged?.(updated)}
                variant="callout"
              />
            )}

            {/* Reassign section (admin only) */}
            {canReview && templates.length > 0 && (
              <div>
                <label style={{
                  fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
                  display: 'block', marginBottom: 6,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  Template
                </label>
                <SearchableSelect
                  value={reassign === '__unclassified__' ? null : reassign}
                  onChange={(v) => setReassign(v ?? '__unclassified__')}
                  options={templates.map(t => ({
                    value: t.id,
                    label: t.label,
                    hint: t.category ? `${t.category}${t.required ? ' · required' : ''}` : (t.required ? 'required' : null),
                  }))}
                  emptyOption={{ value: null, label: '— Unclassified —' }}
                  disabled={!!busy}
                  placeholder="Pick a template"
                />
                {reassignChanged && doc.status !== 'approved' && (
                  <Btn
                    variant="outline"
                    size="sm"
                    onClick={saveReassign}
                    disabled={!!busy}
                    style={{ marginTop: 8, width: '100%' }}
                    icon={busy === 'reassign' ? null : Icon.check}
                  >
                    {busy === 'reassign'
                      ? <><Spinner size={12} /> Saving…</>
                      : 'Save reassignment'}
                  </Btn>
                )}
              </div>
            )}

            {/* Expiration date — only shown if the current template tracks expirations */}
            {(() => {
              const currentTpl = templates.find(t => t.id === (reassign || doc.template_id));
              if (!currentTpl?.has_expiration) return null;
              return (
                <div>
                  <label style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
                    display: 'block', marginBottom: 6,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
                    Expiration date
                  </label>
                  <ExpirationField
                    uploadId={doc.id}
                    expiresAt={doc.expires_at}
                    canEdit={true}
                    onChanged={(newDate) => {
                      onChanged?.({ ...doc, expires_at: newDate });
                    }}
                    variant="inline"
                  />
                </div>
              );
            })()}

            {/* Rejection reason (if rejected) */}
            {doc.status === 'rejected' && doc.admin_notes && (
              <div>
                <label style={{
                  fontSize: 11, fontWeight: 700, color: 'var(--danger)',
                  display: 'block', marginBottom: 6,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  Rejection reason
                </label>
                <div style={{
                  padding: 10,
                  background: 'var(--danger-bg)',
                  border: '1px solid rgba(239,68,68,0.18)',
                  borderRadius: 8,
                  fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5,
                }}>
                  {doc.admin_notes}
                </div>
              </div>
            )}

            {/* Action buttons (admin, only if reviewable status) */}
            {canReview && doc.status !== 'approved' && doc.status !== 'rejected' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 'auto', paddingTop: 12 }}>
                <Btn
                  variant="primary"
                  size="md"
                  icon={busy === 'approve' ? null : Icon.check}
                  onClick={approve}
                  disabled={!!busy}
                  style={{ width: '100%' }}
                >
                  {busy === 'approve'
                    ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Approving…</>
                    : 'Approve'}
                </Btn>
                <Btn
                  variant="outline"
                  size="md"
                  icon={busy === 'reject' ? null : Icon.x}
                  onClick={reject}
                  disabled={!!busy}
                  style={{ width: '100%', color: 'var(--danger)', borderColor: 'rgba(239,68,68,0.3)' }}
                >
                  {busy === 'reject'
                    ? <><Spinner size={14} color="rgba(239,68,68,0.5)" /> Rejecting…</>
                    : 'Reject'}
                </Btn>
              </div>
            )}

            {/* Approved/Rejected banner */}
            {(doc.status === 'approved' || doc.status === 'rejected') && (
              <div style={{
                padding: 10,
                background: doc.status === 'approved' ? 'var(--ok-bg)' : 'var(--danger-bg)',
                border: `1px solid ${doc.status === 'approved' ? 'rgba(61,199,91,0.22)' : 'rgba(239,68,68,0.22)'}`,
                borderRadius: 8,
                fontSize: 12, color: 'var(--text-2)',
                display: 'flex', alignItems: 'center', gap: 8,
                marginTop: 'auto',
              }}>
                <Icon.check size={14} stroke={doc.status === 'approved' ? 'var(--ok)' : 'var(--danger)'} />
                This document has already been {doc.status}.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rejection reason modal — nested on top of the file viewer */}
      {rejectOpen && (
        <div
          onClick={() => !busy && setRejectOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 250,
            background: 'rgba(10, 15, 46, 0.7)',
            display: 'flex',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'center',
            padding: isMobile ? 0 : 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              border: isMobile ? 'none' : '1px solid var(--border)',
              borderRadius: isMobile ? 0 : 'var(--r-xl)',
              boxShadow: 'var(--shadow-xl)',
              width: '100%',
              maxWidth: isMobile ? 'none' : 480,
              height: isMobile ? '100dvh' : 'auto',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{
              padding: '18px 22px',
              borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'var(--danger-bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon.alert size={16} stroke="var(--danger)" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Reject document</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                  The uploader will be notified by email{doc.original_filename ? ` about ${doc.original_filename}` : ''}.
                </div>
              </div>
            </div>

            <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
              <label style={{
                fontSize: 12, fontWeight: 600, color: 'var(--text-2)',
                display: 'block', marginBottom: 6,
              }}>
                Reason for rejection
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                disabled={busy === 'reject'}
                autoFocus
                rows={4}
                placeholder="e.g. The document is blurry — please re-scan and upload a clearer copy."
                className="mi-focusable"
                style={{
                  boxSizing: 'border-box',
                  width: '100%', minHeight: 96, padding: '10px 12px',
                  fontSize: 13.5, fontFamily: 'inherit', color: 'var(--text)',
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-md)', resize: 'vertical',
                  lineHeight: 1.5,
                }}
              />
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                Be specific — this is the only message the uploader will see.
              </div>

              {/* Quick-pick reasons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                {QUICK_REJECT_REASONS.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRejectReason(r)}
                    disabled={busy === 'reject'}
                    style={{
                      padding: '5px 10px', fontSize: 11.5, fontWeight: 500,
                      background: 'var(--surface-2)', color: 'var(--text-2)',
                      border: '1px solid var(--border)', borderRadius: 999,
                      cursor: busy === 'reject' ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div style={{
              padding: '14px 22px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: isMobile ? 'stretch' : 'flex-end',
              gap: 8,
              flexShrink: 0,
              background: 'var(--surface)',
            }}>
              <Btn
                variant="outline"
                size="md"
                onClick={() => setRejectOpen(false)}
                disabled={busy === 'reject'}
                style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
              >
                Cancel
              </Btn>
              <Btn
                variant="primary"
                size="md"
                icon={busy === 'reject' ? null : Icon.x}
                onClick={confirmReject}
                disabled={busy === 'reject' || !rejectReason.trim()}
                style={{
                  background: busy === 'reject' ? undefined : 'var(--danger)',
                  borderColor: 'var(--danger)',
                  ...(isMobile ? { flex: 2, justifyContent: 'center' } : {}),
                }}
              >
                {busy === 'reject'
                  ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Rejecting…</>
                  : 'Reject document'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const QUICK_REJECT_REASONS = [
  'Blurry — please re-scan',
  'Wrong document type',
  'Expired — needs current version',
  'Missing signature',
  'Cut off / incomplete',
];

function NoFileFallback() {
  return (
    <Card style={{ padding: 32, textAlign: 'center', maxWidth: 360 }}>
      <Icon.file size={32} stroke="var(--text-muted)" />
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 12 }}>
        No file URL on this document
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
        The upload may not have completed.
      </div>
    </Card>
  );
}

function UnsupportedFallback({ url, ext }) {
  return (
    <Card style={{ padding: 32, textAlign: 'center', maxWidth: 360 }}>
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        margin: '0 auto 14px',
        background: 'var(--surface-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
        fontFamily: 'var(--font-mono)',
      }}>
        {(ext || 'FILE').toUpperCase()}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        Preview not available
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 14 }}>
        This file type can't be previewed in the browser.
      </div>
      <Btn
        variant="primary"
        size="sm"
        icon={Icon.external}
        onClick={() => window.open(url, '_blank', 'noopener')}
      >
        Open file
      </Btn>
    </Card>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
